import "styles/modal.scss";
import "./CodeDirectoryDrawer.scss";

import cx from "classnames";
import Toggle from "components/inputs/Toggle/Toggle";
import LoadingIcon from "components/Loading/LoadingIcon";
import SearchBar from "components/SearchBar/SearchBar";
import type { ServerSortColumn } from "components/Table/Table";
import Table from "components/Table/Table";
import { commonColumnProps, PAGE_SIZE } from "constants/TableConstants";
import { useMemo, useState } from "react";
import { setShowCPTCodesDrawer } from "reducers/drawer";
import {
  useGetCPTCodesByInputQuery,
  useGetHCPCSCodesByInputQuery,
} from "services/codes-api";
import type { RootState } from "services/store";
import { useQuery } from "utils/hooks";
import { useDispatch, useSelector } from "utils/redux";
import { GLOBAL_SEARCH_PARAM } from "utils/useUrlStore";

type CodeDirectoryDrawerProps = {
  onSelectCode?: (code: string, description: string) => void;
};

type CodeDirectoryEntry = {
  id: number;
  code: string;
  description: string;
  is_internal?: boolean;
};

const CodeDirectoryDrawer = ({ onSelectCode }: CodeDirectoryDrawerProps) => {
  const dispatch = useDispatch();
  const query = useQuery();
  const showCPTCodesDrawer = useSelector(
    (state: RootState) => state.drawer.showCPTCodesDrawer,
  );

  const [page, setPage] = useState(1);
  const [codeType, setCodeType] = useState<"cpt" | "hcpcs">("cpt");
  const [sortColumns, setSortColumns] = useState<readonly ServerSortColumn[]>([
    { columnKey: "code", direction: "ASC" },
  ]);
  const searchTerm = query.get(GLOBAL_SEARCH_PARAM);

  const ordering = sortColumns
    .map(({ columnKey, direction }) => {
      if (codeType === "cpt" && columnKey === "source") {
        // Boolean ascending: false (Universal) first, true (Internal) first on DESC.
        // Invert so the first click produces a visible change from the internal-first default.
        return direction === "ASC" ? "-is_internal" : "is_internal";
      }
      return direction === "DESC" ? `-${columnKey}` : columnKey;
    })
    .join(",");

  const queryArgs = {
    search: searchTerm || undefined,
    ordering,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  };

  const cptResult = useGetCPTCodesByInputQuery(queryArgs, {
    skip: !showCPTCodesDrawer || codeType !== "cpt",
  });
  const hcpcsResult = useGetHCPCSCodesByInputQuery(queryArgs, {
    skip: !showCPTCodesDrawer || codeType !== "hcpcs",
  });

  const { data, isLoading, isFetching } =
    codeType === "cpt" ? cptResult : hcpcsResult;

  const codes = (data?.results || []) as CodeDirectoryEntry[];
  const totalCount = data?.count || 0;

  const codeLabel = codeType === "cpt" ? "CPT" : "HCPCS";

  const columns = useMemo(
    () => [
      {
        key: "code",
        name: `${codeLabel} Code`,
        ...commonColumnProps,
        width: "160px",
        renderCell: ({ row }: { row: CodeDirectoryEntry }) => (
          <div>
            <strong>{row.code}</strong>
          </div>
        ),
      },
      {
        key: "source",
        name: "Type",
        ...commonColumnProps,
        width: "110px",
        sortable: codeType === "cpt",
        renderCell: ({ row }: { row: CodeDirectoryEntry }) =>
          row.is_internal ? (
            <span className="internal-badge internal-badge--table">
              Internal
            </span>
          ) : (
            <span>Universal</span>
          ),
      },
      {
        key: "description",
        name: "Description",
        ...commonColumnProps,
        minWidth: 300,
      },
      ...(onSelectCode
        ? [
            {
              key: "actions",
              name: "Actions",
              ...commonColumnProps,
              width: "100px",
              sortable: false,
              renderCell: ({ row }: { row: CodeDirectoryEntry }) => (
                <div className="flex jc-c">
                  <button
                    className="select-code-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onSelectCode(row.code, row.description);
                    }}
                  >
                    Select
                  </button>
                </div>
              ),
            },
          ]
        : []),
    ],
    [onSelectCode, codeLabel, codeType],
  );

  const renderContent = useMemo(() => {
    if (isLoading) {
      return (
        <div className="documents--Loading">
          <LoadingIcon message={`Loading ${codeLabel} Codes`} />
        </div>
      );
    }

    if (!codes?.length) {
      return (
        <div className="empty-state">
          <div className="empty-state-content">
            <span className="material-icons">medical_services</span>
            <h3>
              {searchTerm
                ? `No ${codeLabel} Codes Found`
                : `No ${codeLabel} Codes Available`}
            </h3>
            <p>
              {searchTerm
                ? "Try adjusting your search criteria"
                : `Start searching for ${codeLabel} codes`}
            </p>
          </div>
        </div>
      );
    }

    return (
      <Table
        className="code-directory-table"
        drawer={showCPTCodesDrawer}
        columns={columns}
        rows={codes}
        rowKeyGetter={(row: CodeDirectoryEntry) =>
          `${row.id}-${row.is_internal ?? ""}`
        }
        isLoading={isFetching}
        useServerSorting={true}
        serverSorting={{
          sortColumns,
          onSortChange: (cols) => {
            setSortColumns(cols);
            setPage(1);
          },
        }}
        pagination={{
          pageSize: PAGE_SIZE,
          pageIndex: page,
          rowCount: totalCount,
          onPageChange: setPage,
          isLoading: isFetching,
        }}
        zebraStripes={true}
        customScrollbars={true}
      />
    );
  }, [
    isLoading,
    codes,
    searchTerm,
    showCPTCodesDrawer,
    columns,
    isFetching,
    page,
    totalCount,
    codeLabel,
    sortColumns,
  ]);

  return (
    <div
      className={cx("documents documents-modal flex jc-se", {
        open: showCPTCodesDrawer,
      })}
    >
      <div className="documents-wrapper w-search">
        <div className="header-primary flex jc-sb ai-c">
          Code Directory
          <div className="header-actions" style={{ display: "flex" }}>
            <div className="code-type-toggle">
              <Toggle
                leftLabel="CPT"
                rightLabel="HCPCS"
                checked={codeType === "hcpcs"}
                onChange={(checked) => {
                  setCodeType(checked ? "hcpcs" : "cpt");
                  setPage(1);
                  setSortColumns([{ columnKey: "code", direction: "ASC" }]);
                }}
              />
            </div>
            <a
              className="header-primary alt-btn"
              onClick={() => dispatch(setShowCPTCodesDrawer(false))}
            >
              Close
            </a>
          </div>
        </div>

        <div className="search-container">
          <SearchBar
            override={true}
            variant="documents"
            filterType="documents"
            showFilterButton={false}
          />
        </div>

        <div className="data-values mrg-top">{renderContent}</div>
      </div>
    </div>
  );
};

export default CodeDirectoryDrawer;
